import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styles from './fancybox.module.scss';
import { ReactComponent as FullScreen } from './assets/fullscreen.svg';
import { ReactComponent as FullScreenExit } from './assets/fullscreen-exit.svg';
import { ReactComponent as Grid } from './assets/grid.svg';
import { ReactComponent as Close } from './assets/close.svg';
import { ReactComponent as ChevronLeft } from './assets/chevron-left.svg';
import { ReactComponent as ChevronRight } from './assets/chevron-right.svg';
import classNames from 'classnames';
import { Photo } from '../../media/Api/Models/Photo';

export type FancyboxProps = {};

export type FancyboxApi = {
  isOpen: boolean;
  addGroupPhoto: (group: string, photoUrl: string) => void;
  addThumbs: (group: string, thumbUrl: string) => void;
  openFancybox: (group: string, index: number) => void;
}


const Fancybox: FunctionComponent<FancyboxProps> = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [group, setGroup] = useState<string>('');
  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [showThumbnails, setShowThumbnails] = useState<boolean>(false);
  const [images, setImages] = useState<Record<string, Array<string>>>({});
  const [thumbs, setThumbs] = useState<Record<string, Array<string>>>({});
  const [currentImageIndex, setCurrentImageIndex] = useState<any>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [renderImage, setRenderImage] = useState<boolean>(false);

  const fancybox = useRef<any>();

  const OPEN_FANCYBOX = 'OPEN_FANCYBOX';

  function determineIfPhoto(toBeDetermined: Photo | string): toBeDetermined is Photo {
    return !!(toBeDetermined as Photo).id;
  }

  const openFancybox = (group: string, index: number = 0) => {
    document.dispatchEvent(new CustomEvent(OPEN_FANCYBOX, { detail: { group, index } }));
  };

  const addGroupPhoto = (group: string, photos: string[] | string) => {
    const arr = images[group] || [];

    if (typeof photos === 'string' && arr.findIndex(element => element === photos) != -1) {
      return;
    }

    if (photos.length === 0) {
      return null;
    }

    setImages(prev => {
      if (Array.isArray(photos)) {
        if (photos.length > 0) {
          return ({
            ...prev,
            [group]: [...arr, ...photos],
          });
        }
      }

      return ({
        ...prev,
        [group]: [...arr, photos as string],
      });
    });
  };

  const addThumbnail = (group: string, thumbs: string[] | string) => {
    const arr = images[group] || [];


    if (typeof thumbs === 'string' && arr.findIndex(element => element === thumbs) != -1) {
      return;
    }

    setThumbs(prev => {
      if (Array.isArray(thumbs)) {
        if (thumbs.length > 0) {
          return ({
            ...prev,
            [group]: [...arr, ...thumbs],
          });
        }
      }

      return ({
        ...prev,
        [group]: [...arr, thumbs as string],
      });
    });
  };

  useEffect(() => {
    document.addEventListener(OPEN_FANCYBOX, (event: any) => {
      setIsOpen(true);

      if (event.detail) {
        setGroup(event.detail.group);
        setCurrentImageIndex(event.detail.index);
      }
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    });
  }, []);


  useEffect(() => {
    if (isOpen && fancybox.current) {
      if (images[group].length === 0) {
        handleClose();
      }
      setTimeout(() => {
        setIsActive(true);

        setTimeout(() => {
          setRenderImage(true);
        }, 300);
      }, 10);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);

    setIsActive(false);
    setShowThumbnails(false);
    setFullScreen(false);
    setRenderImage(false);
  };

  const toggleThumbnails = () => {
    setShowThumbnails(!showThumbnails);
  };

  const getNextImage = () => {
    if (images[group].length === 1) {
      return;
    }

    setLoading(true);
    if (currentImageIndex + 1 > images[group].length - 1) {
      setCurrentImageIndex(0);
    } else {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const getPrevImage = () => {
    if (images[group].length === 1) {
      return;
    }

    setLoading(true);

    if (currentImageIndex - 1 < 0) {
      setCurrentImageIndex(images[group].length - 1);
    } else {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleThumbClick = (index: number) => {
    if (index === currentImageIndex) {
      return;
    }
    setLoading(true);
    setCurrentImageIndex(index);
  };

  const toggleFullScreen = () => {
    if (document.fullscreenElement === fancybox.current) {
      document.exitFullscreen();
      setFullScreen(false);
    } else {
      setFullScreen(true);
      if (fancybox.current.requestFullscreen) {
        fancybox.current.requestFullscreen().then();
      } else if (fancybox.current.webkitRequestFullscreen) { /* Safari */
        fancybox.current.webkitRequestFullscreen();
      } else if (fancybox.current.msRequestFullscreen) { /* IE11 */
        fancybox.current.msRequestFullscreen();
      }
    }
  };

  const toggleLoader = (event: any) => {
    const render = () => {
      setLoading(false);
    };

    const startRender = () => {
      requestAnimationFrame(render);
    };

    if (event.target.complete) {
      requestAnimationFrame(startRender);
    }
  };

  if (window) {
    window.fancyboxAPI = {
      isOpen: false,
      addGroupPhoto,
      addThumbnail,
      openFancybox,
    };
  }

  if (!isOpen || !images[group]) {
    return null;
  }

  return (
    <div className={classNames(styles.fancybox, { [styles.open]: isOpen }, { [styles.animate]: isActive }, { [styles.showThumbnails]: showThumbnails })} ref={fancybox}>
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <div className={styles.actions}>
            <button className={styles.action} onClick={toggleFullScreen}>
              {fullScreen ? <FullScreenExit/> : <FullScreen/>}
            </button>
            <button className={styles.action} onClick={toggleThumbnails}>
              <Grid/>
            </button>
            <button className={styles.action} onClick={handleClose}>
              <Close/>
            </button>
          </div>
        </div>
        <div className={styles.carousel}>
          <div className={styles.viewport}>
            <div className={styles.slides}>
              <div className={styles.slide}>
                <svg xmlns="http://www.w3.org/2000/svg"
                  className={styles.loader} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">
                  <path d="M10 50A40 40 0 0 0 90 50A40 42 0 0 1 10 50" fill="currentColor" stroke="none">
                    <animateTransform attributeName="transform" type="rotate" dur="1s" repeatCount="indefinite" keyTimes="0;1" values="0 50 51;360 50 51"></animateTransform>
                  </path>
                </svg>
                {renderImage && (
                  <img src={images[group][currentImageIndex]}
                    className={classNames({ [styles.loading]: loading })}
                    alt=""
                    onLoad={(event) => toggleLoader(event)}
                    loading="lazy"
                  />
                )}
              </div>
            </div>
          </div>

          {images[group].length > 1 && (
            <div className={styles.navigation}>
              <button className={styles.prev} onClick={getPrevImage}>
                <ChevronLeft/>
              </button>
              <button className={styles.next} onClick={getNextImage}>
                <ChevronRight/>
              </button>
            </div>
          )}
        </div>

        <div className={styles.thumbs}>
          <div className={styles.thumbsContainer}>
            {showThumbnails && thumbs[group].map((element, index) => (
              <div className={styles.thumb}
                key={element + `-${index}`}
                onClick={() => handleThumbClick(index)}
              >
                <img src={element} alt="" loading="lazy"/>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fancybox;
